import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FiTruck, FiArrowLeft } from 'react-icons/fi';
import { OfferRideSummaryBar } from './OfferRideSummaryBar';
import { OfferRidePreviousFlow } from './OfferRidePreviousFlow';
import { OfferRideNewFlow } from './OfferRideNewFlow';

interface StopLocation {
  id: string;
  name: string;
  coordinates: [number, number];
  type: 'pickup' | 'enroute' | 'dropoff';
}

interface RideData {
  from: string;
  to: string;
  pickup: string;
  drop: string;
  date: string;
  time: string;
  car: string;
  price: number;
  preferences: string[];
  seats: number;
  seatPrefs: string;
  baggage: string;
  note: string;
  stopLocations: StopLocation[];
  priceSaved?: number;
}

// Dummy mock data for demonstration
const draftRidesMock: RideData[] = [
  {
    from: 'Mumbai',
    to: 'Pune',
    pickup: '',
    drop: '',
    date: 'Tomorrow',
    time: '6:30 AM',
    car: 'Honda City - MH12AB1234',
    price: 220,
    preferences: [],
    seats: 1,
    seatPrefs: '',
    baggage: '',
    note: '',
    stopLocations: [],
    priceSaved: 25,
  },
  {
    from: 'Pune',
    to: 'Nashik',
    pickup: '',
    drop: '',
    date: 'Friday',
    time: '7:30 AM',
    car: 'Maruti Swift - MH12XY2020',
    price: 200,
    preferences: [],
    seats: 2,
    seatPrefs: '',
    baggage: '',
    note: '',
    stopLocations: [],
    priceSaved: 15,
  }
];

const publishedOffersMock: RideData[] = [
  {
    from: 'Mumbai',
    to: 'Hyderabad',
    pickup: '',
    drop: '',
    date: 'Monday',
    time: '8:00 AM',
    car: 'Maruti Swift - MH12CD5678',
    price: 350,
    preferences: ['Music', 'AC'],
    seats: 3,
    seatPrefs: '',
    baggage: 'Large',
    note: 'Smooth journey assured!',
    stopLocations: [],
    priceSaved: 45,
  },
  {
    from: 'Pune',
    to: 'Goa',
    pickup: '',
    drop: '',
    date: 'Wednesday',
    time: '6:00 AM',
    car: 'Honda Jazz - MH14JJ6655',
    price: 400,
    preferences: ['Music'],
    seats: 4,
    seatPrefs: '',
    baggage: 'Medium',
    note: '',
    stopLocations: [],
    priceSaved: 30,
  },
  {
    from: 'Nagpur',
    to: 'Indore',
    pickup: '',
    drop: '',
    date: 'Sunday',
    time: '9:00 AM',
    car: 'Swift Dzire - MH31ZQ1111',
    price: 300,
    preferences: [],
    seats: 4,
    seatPrefs: '',
    baggage: 'Small',
    note: 'Fun trip!',
    stopLocations: [],
    priceSaved: 20,
  },
  // Second row
  {
    from: 'Delhi',
    to: 'Jaipur',
    pickup: '',
    drop: '',
    date: 'Saturday',
    time: '7:15 AM',
    car: 'Hyundai i20 - DL2CA7777',
    price: 250,
    preferences: ['No Smoking'],
    seats: 3,
    seatPrefs: '',
    baggage: '',
    note: '',
    stopLocations: [],
    priceSaved: 35,
  },
  {
    from: 'Bangalore',
    to: 'Mysore',
    pickup: '',
    drop: '',
    date: 'Friday',
    time: '5:20 AM',
    car: 'Toyota Innova - KA05DE1919',
    price: 430,
    preferences: [],
    seats: 7,
    seatPrefs: 'Window',
    baggage: 'Huge',
    note: '',
    stopLocations: [],
    priceSaved: 50,
  }
];

export const OfferRideFlow: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [flowType, setFlowType] = useState<'previous'|'new'|null>(null);
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(null);
  const [selectedPublishedId, setSelectedPublishedId] = useState<number | null>(null);

  // Quick Edit (Previous or from Card)
  if (flowType === 'previous' && (selectedDraftId !== null || selectedPublishedId !== null)) {
    const offer =
      selectedDraftId !== null
        ? draftRidesMock.find(d => d.from === draftRidesMock[selectedDraftId]?.from)
        : publishedOffersMock.find(d => d.from === publishedOffersMock[selectedPublishedId]?.from);
    // fallback to first if not found
    const draftOffer = offer || draftRidesMock[0];
    return (
      <OfferRidePreviousFlow
        draftOffer={draftOffer}
        onBack={() => {
          setFlowType(null);
          setSelectedDraftId(null);
          setSelectedPublishedId(null);
        }}
      />
    );
  }

  // New Ride Flow
  if (flowType === 'new') {
    return (
      <OfferRideNewFlow
        onBack={() => {
          setFlowType(null);
          setSelectedDraftId(null);
          setSelectedPublishedId(null);
        }}
      />
    );
  }

  // Main entry: New structure.
  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col">
      {/* Topbar */}
      <div className="flex items-center p-4 border-b justify-between">
        <Button variant="ghost" onClick={onBack} size="sm" className="p-2">
          <FiArrowLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-lg font-semibold text-gray-800 flex-1 text-center">Offer a Ride</h2>
        <Button
          className="ml-auto"
          onClick={() => setFlowType('new')}
          size="sm"
          variant="default"
        >
          + Offer New Ride
        </Button>
      </div>

      <div className="p-4 flex-1 overflow-y-auto">
        {/* Section 1: Draft Offers */}
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Draft Offers</h3>
        <div className="flex gap-4 overflow-x-auto pb-2 mb-6">
          {draftRidesMock.length === 0 ? (
            <div className="text-gray-500 italic text-sm mb-4">No draft ride offers</div>
          ) : (
            draftRidesMock.map((draft, index) => (
              <Card key={index} className="min-w-[260px] flex-shrink-0 relative border-2 border-yellow-200">
                <CardContent className="p-3 flex flex-col justify-between h-full">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FiTruck className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-[15px]">
                        {draft.from} → {draft.to}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      {draft.date} {draft.time}
                    </div>
                    <div className="text-xs text-gray-600 mb-1">
                      {draft.car}
                    </div>
                    <div className="flex items-center mt-2">
                      <span className="bg-green-100 text-green-700 text-xs rounded px-2 py-0.5 font-medium">
                        ₹{draft.price}
                      </span>
                      {draft.priceSaved ? (
                        <span className="ml-3 flex items-center text-[11px] text-blue-700 bg-blue-50 rounded px-2 py-0.5">
                          <span className="font-semibold">Price Saved:</span> +₹{draft.priceSaved}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex justify-between items-end mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setFlowType('previous');
                        setSelectedDraftId(index);
                        setSelectedPublishedId(null);
                      }}
                    >
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Section 2: Published Offers */}
        <h3 className="text-sm font-semibold text-gray-800 mt-2 mb-2">Published Offers</h3>
        <div className="grid grid-rows-2 gap-4">
          {/* Build two rows, each with up to 3 cards */}
          <div className="flex gap-4">
            {publishedOffersMock.slice(0, 3).map((pub, index) => (
              <Card key={index} className="w-[250px] relative border">
                <CardContent className="p-3 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-2">
                    <FiTruck className="w-5 h-5 text-indigo-700" />
                    <span className="font-semibold text-[15px]">{pub.from} → {pub.to}</span>
                  </div>
                  <div className="text-xs text-gray-600">{pub.date} {pub.time}</div>
                  <div className="text-xs text-gray-600">{pub.car}</div>
                  <div className="flex items-center mt-2">
                    <span className="bg-green-50 text-green-800 text-xs rounded px-2 py-0.5 font-medium">
                      ₹{pub.price}
                    </span>
                    {pub.priceSaved ? (
                      <span className="ml-2 flex items-center text-[11px] text-blue-700 bg-blue-50 rounded px-2 py-0.5">
                        <span className="font-semibold">Price Saved:</span> +₹{pub.priceSaved}
                      </span>
                    ) : null}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setFlowType('previous');
                      setSelectedDraftId(null);
                      setSelectedPublishedId(index);
                    }}
                  >
                    Same as this
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex gap-4">
            {publishedOffersMock.slice(3, 6).map((pub, index) => (
              <Card key={index + 3} className="w-[250px] relative border">
                <CardContent className="p-3 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-2">
                    <FiTruck className="w-5 h-5 text-indigo-700" />
                    <span className="font-semibold text-[15px]">{pub.from} → {pub.to}</span>
                  </div>
                  <div className="text-xs text-gray-600">{pub.date} {pub.time}</div>
                  <div className="text-xs text-gray-600">{pub.car}</div>
                  <div className="flex items-center mt-2">
                    <span className="bg-green-50 text-green-800 text-xs rounded px-2 py-0.5 font-medium">
                      ₹{pub.price}
                    </span>
                    {pub.priceSaved ? (
                      <span className="ml-2 flex items-center text-[11px] text-blue-700 bg-blue-50 rounded px-2 py-0.5">
                        <span className="font-semibold">Price Saved:</span> +₹{pub.priceSaved}
                      </span>
                    ) : null}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setFlowType('previous');
                      setSelectedDraftId(null);
                      setSelectedPublishedId(index + 3);
                    }}
                  >
                    Same as this
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
